import { Component, EventEmitter, OnDestroy, Output, Input, OnInit, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl, Validators } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-titan-input',
  templateUrl: './titan-input.component.html',
  standalone: true,
  styleUrls: ['./titan-input.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule
  ]
})
export class TitanInputComponent implements OnInit, OnChanges, OnDestroy {
  @Input() initialValue: string = '';
  @Input() placeholder: string = 'Search...';
  @Input() icon: string = '';
  @Input() maxLength?: number;
  @Input() required?: boolean;
  
  searchText: string = '';
  inputControl = new FormControl('');

  @Output() onChange = new EventEmitter<string>();
  @Output() onValidationChange = new EventEmitter<boolean>();

  // Subject to capture input changes.
  private inputSubject = new Subject<string>();
  private onChangeSubscription: Subscription;

  constructor() {
    // Debounce user input by 300ms and ignore duplicate consecutive values.
    this.onChangeSubscription = this.inputSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(value => {
      this.onChange.emit(value);
    });
  }

  ngOnInit(): void {
    if (this.initialValue) {
      this.searchText = this.initialValue;
      this.inputControl.setValue(this.initialValue);
    }
    this.setupValidators();
    this.emitValidationState();
  }

  ngOnChanges(): void {
    if (this.initialValue !== undefined) {
      this.searchText = this.initialValue;
      this.inputControl.setValue(this.initialValue);
    }
    this.setupValidators();
    this.emitValidationState();
  }

  private setupValidators(): void {
    const validators = [];
    
    if (this.required) {
      validators.push(Validators.required);
    }
    
    if (this.maxLength) {
      validators.push(Validators.maxLength(this.maxLength));
    }
    
    this.inputControl.setValidators(validators);
    this.inputControl.updateValueAndValidity();
  }

  private emitValidationState(): void {
    this.onValidationChange.emit(this.inputControl.valid);
  }

  onInputChange(event: Event) {
    this.searchText = (event.target as HTMLInputElement).value;
    this.inputControl.setValue(this.searchText);
    this.inputControl.markAsTouched();
    this.inputSubject.next(this.searchText);
    this.emitValidationState();
  }

  ngOnDestroy(): void {
    this.onChangeSubscription.unsubscribe();
  }

  clearSearch() {
    this.searchText = '';
    this.inputControl.setValue('');
    this.onChange.emit('');
    this.emitValidationState();
  }
}
